import classNames from "classnames";
import { CSSProperties, FC, useMemo, useState } from "react";
import { FaPlus } from "react-icons/fa";

import "./GradientCreator.module.scss";
import { ColorPicker } from "../ColorPicker/ColorPicker";
import { RiRefreshLine } from "react-icons/ri";

interface GradientStop {
    color: string;
}

const convertIntoTenStopGradient = (stops: GradientStop[], divisions = 10): GradientStop[] => {
    if (stops.length <= 1) {
        return stops;
    }

    const canvas = document.createElement("canvas");
    canvas.width = 10 * divisions;
    canvas.height = 1;
    const ctx = canvas.getContext("2d");

    if (!ctx) return [];

    const gradient = ctx.createLinearGradient(0, 0, canvas.width, 0);
    stops.forEach((stop, idx) => {
        gradient.addColorStop(idx / (stops.length - 1), stop.color);
    });
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const newStops: GradientStop[] = [];
    newStops.push({
        color: stops[0].color,
    });
    for (let i = 1; i < divisions; i++) {
        const data = ctx.getImageData(i * 10 + 5, 0, 1, 1).data;
        const hexColor = `#${data[0].toString(16).padStart(2, "0")}${data[1].toString(16).padStart(2, "0")}${data[2].toString(16).padStart(2, "0")}`;
        newStops.push({ color: hexColor });
    }
    newStops.push({
        color: stops[stops.length - 1].color,
    });
    
    return newStops;
}

export const conicTransform = (stops: GradientStop[], seamless: boolean) => {
    if (stops.length <= 1) {
        return stops[0] ? stops[0].color : "#000000";
    }
    if (seamless) {
        stops = [...stops, ...stops.slice(0, stops.length - 1).reverse()];
    }
    const normalized = convertIntoTenStopGradient(stops);
    const stopString = normalized
        .map((stop, idx, arr) => `${stop.color} ${100 / (arr.length-1) * idx}%`)
        .join(", ");

    return `conic-gradient(from calc(360deg * var(--progress)) at 50% 50%, ${stopString}) 1 1`;
}

interface GradientCreatorProps {
    speed?: number;
    borderWidth?: number;
    transformFunc?: (stops: GradientStop[], seamless: boolean) => string;
}

export const GradientCreator: FC<GradientCreatorProps> = ({ speed = 10, borderWidth = 10, transformFunc = conicTransform }) => {
    const [stops, setStops] = useState<GradientStop[]>([
        { color: "#ff0000" },
        { color: "#00ff00" },
        { color: "#0000ff" },
    ]);
    const [seamless, setSeamless] = useState(true);

    const gradientString = useMemo(() => transformFunc(stops, seamless), [stops, seamless, transformFunc]);

    const gradientStyle: CSSProperties = {
        borderWidth: `${borderWidth}px`,
        borderStyle: "solid",
        borderImage: gradientString,
        animation: `anim ${speed}s linear 0s infinite normal forwards`,
    };

    return <div className="w-full flex flex-row items-center justify-center p-4" style={gradientStyle}>
        <div className="flex flex-row justify-stretch items-center gap-1">
            {stops.map((stop, index) => (
                <ColorPicker key={`stop-${index}`} color={stop.color} onChange={(c) => {
                    const newStops = [...stops];
                    newStops[index] = { color: c };
                    setStops(newStops);
                }}/>
            ))}
            <button
                onClick={() => setStops([...stops, { color: "#ffffff" }])}
                className={classNames(
                    "w-10 h-10 ml-5 flex items-center justify-center rounded",
                    "bg-blue-500 hover:bg-blue-600 text-white"
                )}
            >
                <FaPlus />
            </button>
            <button onClick={() => setSeamless(!seamless)} className={classNames("w-10 h-10 flex items-center justify-center rounded", seamless ? "bg-blue-500 hover:bg-blue-600 text-white opacity-100" : "bg-blue-900 hover:bg-blue-800 text-gray-300 opacity-50")}>
                <RiRefreshLine/>
            </button>
        </div>
    </div>;
}